import {useEffect, useRef, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {type StepDefinition, useJobsSimulationConfiguration} from '@tinct/web-components';
import {tinctApi, useGetOrganizationById} from '@tinct/core-api';
import {useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useBrandExtractionJobAlert} from './use-brand-extraction-job-alert';
import {useFunnel} from '@tinct/web-funnel';
import {awaitedJob, type FollowedExtraction, followOnLoad} from '../../../followed-extraction';
import type {BrandExtractionFunnelState} from '../../../brand-extraction-funnel';

export function useBrandExtractionJob(brandSteps: StepDefinition[]) {
  const workspaceId = useGetCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const hasStartedRef = useRef(false);
  const [sseCompleted, setSseCompleted] = useState(false);

  const {data: organization} = useGetOrganizationById({
    id: workspaceId,
    fields: {
      // The organization's latest extraction. Not `lastBrandVersion.extractionJob`:
      // a running extraction has no brand version yet, so that one is the job
      // that produced the previous version.
      brandExtractionJob: {
        fields: {
          id: null,
          startedAt: null,
          status: null,
        },
      },
    },
  }, {
    refetchInterval: 1000,
    enabled: !!workspaceId,
  });

  const latestJob = organization?.brandExtractionJob ?? null;

  // Which extraction this step follows, decided on the organization's first load (see followOnLoad).
  // When the funnel recorded the job that was the latest as the extraction was asked for, that one is
  // ignored, so the funnel and this step wait for the same extraction.
  const {state} = useFunnel<BrandExtractionFunnelState>();
  const recordedPreviousJobId = state?.previousExtractionJobId;
  const [followed, setFollowed] = useState<FollowedExtraction | null>(null);
  if (organization && !followed) {
    const onLoad = followOnLoad(latestJob);
    setFollowed(
      recordedPreviousJobId !== undefined && onLoad.startNew
        ? {startNew: true, previousJobId: recordedPreviousJobId}
        : onLoad,
    );
  }

  // The job of the extraction this step follows, once it exists.
  const job = followed ? awaitedJob(latestJob, followed.previousJobId) : null;
  const jobCompleted = job?.status === 'completed';
  const isAnalysisCompleted = sseCompleted || jobCompleted;

  // The extracted brand lands on `organization.lastBrandVersion`, which other
  // screens (funnel steps, brand settings) read through their own
  // `['organization', id, fields]` entries. With the global `staleTime:
  // Infinity` those entries would keep the pre-extraction version forever, so
  // drop them all once the analysis is done — again when the job itself reads
  // completed, in case the stream ended before the new version was visible.
  useEffect(() => {
    if ((sseCompleted || jobCompleted) && workspaceId) {
      queryClient.invalidateQueries({queryKey: ['organization', workspaceId]});
    }
  }, [sseCompleted, jobCompleted, workspaceId, queryClient]);

  const {start: startJobAlert, gameModal} = useBrandExtractionJobAlert();

  const jobId = job?.id;
  useEffect(() => {
    if (jobId) {
      startJobAlert(jobId);
    }
  }, [jobId, startJobAlert]);

  // Start the brand extraction job once, unless one is already under way.
  const startNew = followed?.startNew ?? false;
  useEffect(() => {
    if (!hasStartedRef.current && workspaceId && startNew) {
      hasStartedRef.current = true;
      tinctApi.brands.initBrandAnalyserAgent(
        workspaceId,
        (event) => {
          if (event && event.type === 'message_stop') {
            setSseCompleted(true);
          }
        },
        (error) => {
          console.error('Error in Brand Analyser Agent:', error.errorCode);
        }
      );
    }
  }, [workspaceId, startNew]);

  const {subJobs, allCompleted, remainingSeconds} = useJobsSimulationConfiguration({
    steps: brandSteps,
    isAnalysisCompleted,
    startedAt: job?.startedAt,
  });

  return {subJobs, allCompleted, remainingSeconds, gameModal};
}
